/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class VisDefinitionsAPI
 * @hideconstructor
 *
 * @classdesc
 * Represents the API to access the visualization definitions that are registered in the dashboard application
 */
define([
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../VisDefinitionsAPI',
	'../InternalVisDefinitionsAPI',
	'../VisDefinitionsAPISpec',
	'../../../../../api/VisDefinitionAPI',
	'../../../../../api/impl/VisDefinition',
	'../../../../../visualizations/definitions/VisDefinitionManager',
	'../../../../../widgets/livewidget/util/VisUtil'
], (APIFactory, VisDefinitionsAPI, InternalVisDefinitionsAPI, VisDefinitionsAPISpec, VisDefinitionAPI, VisDefinitionImpl, VisDefinitionManager, VisUtil) => {

	class VisDefinitions extends VisDefinitionsAPISpec {

		constructor(options) {
			super();
			this.dashboard = options.features.API;
			// TODO : VisDefinitionManager should go away and the implementation should belong to this feature implementation
			this.visDefinitionManager = new VisDefinitionManager({
				dashboardApi: this.dashboard,
				ajax: options.features.Ajax || /*Temporary until perspective file is delivered to INT server*/ this.dashboard.getGlassCoreSvc('.Ajax'),
				preFetch: options.features.PreFetch
			});
			this.internalAPI = APIFactory.createAPI(this, [VisDefinitionsAPI, InternalVisDefinitionsAPI]);
			this.api = APIFactory.createAPI(this, [VisDefinitionsAPI]);

			this._definitionAPIMap  = null;
			this._definitionsJSON = null;
			this._modifiers = [];
		}

		getAPI(type) {
			if(type === 'internal') {
				return this.internalAPI;
			} else {
				return this.api;
			}
		}

		destroy() {
			if (this._definitionAPIMap){
				for (let id in this._definitionAPIMap.implById) {
					this._definitionAPIMap.implById[id].destroy();
					delete this._definitionAPIMap.implById[id];
				}
			}

			this._definitionsJSON = null;
			this._definitionAPIMap = null;

			this.dashboard = null;
			this.visDefinitionManager = null;
			this.internalAPI = null;
			this.api = null;
			this._modifiers = null;
		}

		initialize() {
			if (this._definitionAPIMap){
				return Promise.resolve(this._definitionAPIMap);
			} else{
				return this.visDefinitionManager.getAll(this.dashboard, true /*optimize*/).then((definitions => {
					this._definitionsJSON = definitions;
					return this._createDefinitionAPIMap(definitions);
				}));
			}
		}

		_createDefinitionAPIMap(definitions = {}) {
			this._definitionAPIMap = definitions.jsonDefinitions.reduce((map, definition) => {
				definition = this.modifyDefinition(definition);
				const impl = new VisDefinitionImpl(definition, this.visDefinitionManager);
				const definitionAPI = APIFactory.createAPI(impl, [VisDefinitionAPI]);
				this._addToDefinitionMap(map, impl, definitionAPI);
				return map;
			}, { implById: {}, byId:{}, byType:{}, list:[] });
			return this._definitionAPIMap;
		}

		_addToDefinitionMap(definitionAPIMap, definitionImpl, definitionApi) {
			const id = definitionApi.getId();
			definitionAPIMap.implById[id] = definitionImpl;
			definitionAPIMap.byId[id] =  definitionApi;
			definitionAPIMap.byType[definitionApi.getType() || id] =  definitionApi;
			definitionAPIMap.list.push(definitionApi);

			// ensure the events are propagated to the parent
			APIFactory.setParentChildRelation(this, definitionImpl);
		}

		getList() {
			const list =  (this._definitionAPIMap && this._definitionAPIMap.list) || [];
			/**
			 * livewidget-cleanup [todo] need to revisit here:
			 * should we always filter out the definition with error state or just for empty definition ?
			 * currently, we only set error for empty definition..
			 */
			return list.filter(definition=>!definition.getState().getError());
		}

		loadById(id) {
			return this.visDefinitionManager.get(id);
		}

		getById(id) {
			let visDefinition = this._definitionAPIMap && this._definitionAPIMap.byId[id];
			if (!visDefinition) {
				const visDefinitionImpl = new VisDefinitionImpl({ id: id, type: id }, this.visDefinitionManager);
				visDefinition = visDefinitionImpl.getAPI();
				this._addToDefinitionMap(this._definitionAPIMap, visDefinitionImpl, visDefinition);
				visDefinitionImpl.setError(VisUtil.createVisDefinitionLoadingError(id));
			}
			return visDefinition;
		}

		getByType(type) {
			return this._definitionAPIMap && this._definitionAPIMap.byType[type];
		}

		refresh() {
			return this.visDefinitionManager.refreshProviders(this.dashboard).then((visDefinitions) => {
				this._definitionsJSON = visDefinitions;
				this._createDefinitionAPIMap(visDefinitions);
			});
		}

		// Internal API
		getAll() {
			return this.visDefinitionManager.getAll(this.dashboard);
		}

		// Internal API
		getRawDefinition(id) {
			const definitionImpl = this._definitionAPIMap.implById[id];
			let error = definitionImpl && definitionImpl.getState().getError();
			if (!error && !definitionImpl) {
				error = VisUtil.createVisDefinitionLoadingError(id);
			}
			if (error) {
				throw error;
			}
			return this._definitionAPIMap.implById[id].visSpec;
		}

		modifyDefinition(definition) {
			if (this._modifiers.length > 0){
				// The definition object may be frozen
				definition = JSON.parse(JSON.stringify(definition));
				this._modifiers.forEach(modifier => {
					try {
						modifier.modifyDefinition(definition);
					} catch (e){
						// a bad extension.. ignore it.
					}
				});
			}
			return definition;
		}

		registerModifier(modifier) {
			if ( this._modifiers.indexOf(modifier) === -1 && typeof(modifier.modifyDefinition) === 'function'){
				this._modifiers.push(modifier);
				if (this._definitionsJSON) {
					this._createDefinitionAPIMap(this._definitionsJSON);
				}
			}
		}

		deregisterModifier(modifier) {
			const idx = this._modifiers.indexOf(modifier);
			if ( idx !== -1 ) {
				this._modifiers.splice(idx, 1);
				if (this._definitionsJSON) {
					this._createDefinitionAPIMap(this._definitionsJSON);
				}
			}
		}

	}
	return VisDefinitions;
});
